import { useEffect, useState, useCallback } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  BadgeCheck,
  Crown,
  Ban,
  ShieldAlert,
  Trash2,
  X,
  MapPin,
  Briefcase,
  Flag,
  CalendarClock,
} from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "@/lib/api";
import { Avatar, Tag, Spinner, PremiumBadge, formatDate } from "@/components/shared";
import { PromptDialog, ConfirmDialog } from "@/components/dialogs";
import { SubscriptionDialog } from "@/components/SubscriptionDialog";
import { OfficialVouchDialog } from "@/components/OfficialVouchDialog";

export function UserDetailSheet({ uid, open, onOpenChange, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // 'ban' | 'warn' | 'delete'
  const [subOpen, setSubOpen] = useState(false);
  const [vouchOpen, setVouchOpen] = useState(false);

  const load = useCallback(async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const d = await api.userDetail(uid);
      setData(d);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    if (open && uid) load();
  }, [open, uid, load]);

  const u = data?.user;

  const act = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto bg-card"
          data-testid="user-detail-sheet"
        >
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">Member profile</SheetTitle>
          </SheetHeader>

          {loading || !u ? (
            <Spinner />
          ) : (
            <div className="mt-4 space-y-6">
              {/* Identity */}
              <div className="flex items-start gap-4">
                <Avatar src={u.photos?.[0] || u.photo} name={u.name} size={72} />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-xl truncate">
                    {u.name}
                    {u.age ? `, ${u.age}` : ""}
                  </div>
                  <div className="text-sm text-muted-foreground truncate">{u.email}</div>
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {u.verified && <Tag tone="green"><BadgeCheck className="h-3 w-3" />Verified</Tag>}
                    <PremiumBadge user={u} />
                    {u.banned && <Tag tone="red"><Ban className="h-3 w-3" />Banned</Tag>}
                    <Tag tone={u.role === "admin" ? "primary" : "muted"}>{u.role}</Tag>
                  </div>
                </div>
              </div>

              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Meta icon={MapPin} label="Location" value={[u.city, u.country].filter(Boolean).join(", ") || "—"} />
                <Meta icon={Briefcase} label="Intention" value={u.intention || "—"} />
              </div>

              {/* Subscription */}
              <div className="rounded-lg bg-secondary/40 px-3 py-2.5 flex items-center justify-between gap-3" data-testid="detail-subscription">
                <div className="flex items-center gap-2.5 min-w-0">
                  <Crown className={`h-4 w-4 shrink-0 ${u.premium ? "text-amber-300" : "text-muted-foreground"}`} />
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <CalendarClock className="h-3 w-3" /> Subscription
                    </div>
                    <div className="text-sm truncate">
                      {u.premium ? (
                        <>
                          Premium{u.premium_plan ? ` · ${u.premium_plan}` : ""}
                          {u.premium_expires_at ? ` — expires ${formatDate(u.premium_expires_at)}` : ""}
                        </>
                      ) : (
                        "Free plan"
                      )}
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => setSubOpen(true)} data-testid="detail-manage-subscription">
                  Manage
                </Button>
              </div>
              {u.bio && (
                <div className="text-sm">
                  <div className="text-muted-foreground mb-1">Bio</div>
                  <p className="leading-relaxed">{u.bio}</p>
                </div>
              )}
              {u.interests?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {u.interests.map((it) => (
                    <Tag key={it}>{it}</Tag>
                  ))}
                </div>
              )}
              {u.ban_reason && (
                <div className="rounded-lg bg-red-500/10 ring-1 ring-red-500/30 px-3 py-2 text-sm text-red-200">
                  <span className="font-semibold">Ban reason:</span> {u.ban_reason}
                </div>
              )}

              {/* Photos */}
              <div>
                <div className="text-sm text-muted-foreground mb-2">
                  Photos ({u.photos?.length || 0})
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(u.photos || []).map((p) => (
                    <div key={p} className="relative group aspect-[3/4] rounded-lg overflow-hidden bg-secondary ring-1 ring-border/60">
                      <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                      <button
                        onClick={() =>
                          act(() => api.removePhoto(uid, p), "Photo removed")
                        }
                        className="absolute top-1 right-1 h-6 w-6 grid place-items-center rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive"
                        data-testid="remove-photo-btn"
                        title="Remove photo"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                  {(!u.photos || u.photos.length === 0) && (
                    <div className="col-span-3 text-sm text-muted-foreground py-4 text-center rounded-lg bg-secondary/40">
                      No photos
                    </div>
                  )}
                </div>
              </div>

              {/* Reports against */}
              {data.reports?.length > 0 && (
                <div>
                  <div className="text-sm text-muted-foreground mb-2 flex items-center gap-1.5">
                    <Flag className="h-3.5 w-3.5" /> Reports against this user ({data.reports.length})
                  </div>
                  <div className="space-y-2">
                    {data.reports.map((r) => (
                      <div key={r.report_id} className="rounded-lg bg-secondary/50 px-3 py-2 text-sm flex items-center justify-between">
                        <span>{r.reason || "No reason"}</span>
                        <Tag tone={r.resolved ? "green" : "amber"}>
                          {r.resolved ? "Resolved" : "Open"}
                        </Tag>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator />

              {/* Actions */}
              <div className="grid grid-cols-2 gap-2" data-testid="detail-actions">
                <Button
                  variant="outline"
                  onClick={() =>
                    act(() => api.verify(uid, !u.verified), u.verified ? "Verification revoked" : "User verified")
                  }
                  data-testid="detail-verify"
                >
                  <BadgeCheck className="h-4 w-4" /> {u.verified ? "Unverify" : "Verify"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setSubOpen(true)}
                  data-testid="detail-premium"
                >
                  <Crown className="h-4 w-4" /> Subscription
                </Button>
                <Button
                  variant="outline"
                  className="col-span-2 border-primary/40 text-primary-foreground hover:bg-primary/15"
                  onClick={() => setVouchOpen(true)}
                  data-testid="detail-official-vouch"
                >
                  <BadgeCheck className="h-4 w-4" /> Give official Amoreska vouch
                </Button>
                <Button variant="outline" onClick={() => setDialog("warn")} data-testid="detail-warn">
                  <ShieldAlert className="h-4 w-4" /> Warn
                </Button>
                {u.banned ? (
                  <Button
                    variant="outline"
                    onClick={() => act(() => api.unban(uid), "User unbanned")}
                    data-testid="detail-unban"
                  >
                    <Ban className="h-4 w-4" /> Unban
                  </Button>
                ) : (
                  <Button variant="destructive" onClick={() => setDialog("ban")} data-testid="detail-ban">
                    <Ban className="h-4 w-4" /> Ban
                  </Button>
                )}
                <Button
                  variant="destructive"
                  className="col-span-2"
                  onClick={() => setDialog("delete")}
                  data-testid="detail-delete"
                >
                  <Trash2 className="h-4 w-4" /> Delete user permanently
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <PromptDialog
        open={dialog === "ban"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Ban user"
        description={`Ban ${u?.name || "this user"}? They will lose access.`}
        confirmText="Ban user"
        destructive
        onConfirm={(reason) => act(() => api.ban(uid, reason), "User banned")}
      />
      <PromptDialog
        open={dialog === "warn"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Warn user"
        description={`Send a warning to ${u?.name || "this user"}.`}
        confirmText="Send warning"
        onConfirm={(reason) => act(() => api.warn(uid, reason), "Warning issued")}
      />
      <ConfirmDialog
        open={dialog === "delete"}
        onOpenChange={(v) => !v && setDialog(null)}
        title="Delete user permanently?"
        description="This cannot be undone. The user and their data will be removed."
        confirmText="Delete permanently"
        destructive
        onConfirm={async () => {
          await api.deleteUser(uid);
          toast.success("User deleted");
          onOpenChange(false);
          onChanged?.();
        }}
      />
      <SubscriptionDialog
        open={subOpen}
        onOpenChange={setSubOpen}
        user={u}
        onDone={() => {
          load();
          onChanged?.();
        }}
      />
      <OfficialVouchDialog open={vouchOpen} onOpenChange={setVouchOpen} user={u} onDone={load} />
    </>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-secondary/40 px-3 py-2">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-0.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="truncate">{value}</div>
    </div>
  );
}
